import type {
  ErrorUserFacingDetails,
  ErrorUserFacingDetailsWithMessage,
} from "schemas/generated/errors";
import type { ReploErrorConfig } from "./reploError";

import mapValues from "lodash-es/mapValues";
import { randomUUID } from "replo-utils/lib/crypto";
import { getErrorMessage } from "replo-utils/lib/errors";
import { exhaustiveSwitch, hasOwnProperty } from "replo-utils/lib/misc";
import * as z from "zod";

import { ReploError } from "./reploError";

/**
 * Wraps an error so `async-retry` stops immediately instead of scheduling
 * another attempt: the library rejects with any thrown error carrying a truthy
 * `bail` property. Preserves the underlying error's message and keeps it as the
 * cause so callers still surface the real failure.
 */
export class AsyncRetryBailError extends ReploError {
  readonly bail = true;

  constructor(cause: unknown) {
    super({
      message: cause instanceof Error ? cause.message : String(cause),
      cause,
    });
  }
}

export type AuthErrorType = "unauthenticated" | "unauthorized" | "notFound";

export type ReploAuthenticationErrorConfig = {
  message: string;
  type: AuthErrorType;
  userFacingDetails?: ErrorUserFacingDetailsWithMessage;
  additionalData?: ReploErrorConfig["additionalData"];
  cause?: ReploErrorConfig["cause"];
};

export class ReploAuthenticationError extends ReploError {
  type: AuthErrorType;
  userFacingDetails?: ErrorUserFacingDetailsWithMessage;

  constructor(config: ReploAuthenticationErrorConfig) {
    super({
      message: config.message,
      additionalData: {
        ...config.additionalData,
        authErrorType: config.type,
      },
      cause: config.cause,
    });
    this.type = config.type;
    this.userFacingDetails = config.userFacingDetails;
  }

  getHttpStatusCode(): 401 | 403 | 404 {
    return exhaustiveSwitch({ type: this.type })({
      unauthenticated: () => 401 as const,
      unauthorized: () => 403 as const,
      notFound: () => 404 as const,
    });
  }

  getTRPCErrorCode(): "UNAUTHORIZED" | "FORBIDDEN" | "NOT_FOUND" {
    return exhaustiveSwitch({ type: this.type })({
      unauthenticated: () => "UNAUTHORIZED" as const,
      unauthorized: () => "FORBIDDEN" as const,
      notFound: () => "NOT_FOUND" as const,
    });
  }
}

/**
 * Error which automatically makes all Replo services respond with a specific
 * HTTP status code when caught. Should only be thrown from routes, makes it
 * easier to provide a standardized way of implementing user-facing messages
 */
export class ReploHTTPError extends ReploError {
  userFacingDetails: ErrorUserFacingDetails;
  statusCode: number;
  details?: object;

  constructor(
    config: ReploErrorConfig & {
      statusCode: number;
      userFacingDetails: ErrorUserFacingDetails;
      details?: object;
    },
  ) {
    super({
      ...config,
      message: config.message ?? config.userFacingDetails.message,
      additionalData: {
        ...config.additionalData,
        statusCode: config.statusCode,
      },
    });
    this.statusCode = config.statusCode;
    this.userFacingDetails = config.userFacingDetails;
    this.details = config.details;
  }
}

/**
 * HTTP error for callers that branch on `key` in code (SDKs, other services)
 * rather than showing it in Replo's own UI, so it has no display `type`. The
 * worker error handler answers `{ key, message, details, errorId }`. Should
 * only be thrown from routes.
 */
export class ReploParseableHTTPError extends ReploError {
  statusCode: number;
  key: string;
  userFacingMessage: string;
  details?: Record<string, unknown>;

  constructor(
    config: ReploErrorConfig & {
      statusCode: number;
      key: string;
      userFacingMessage: string;
      details?: Record<string, unknown>;
    },
  ) {
    super({
      ...config,
      message: config.message ?? config.userFacingMessage,
      additionalData: {
        ...config.additionalData,
        statusCode: config.statusCode,
        key: config.key,
      },
    });
    this.statusCode = config.statusCode;
    this.key = config.key;
    this.userFacingMessage = config.userFacingMessage;
    this.details = config.details;
  }
}

/**
 * Error used to represent an HTTP error that should be surfaced as such. This
 * should be thrown when we want to pass the error through without changing the
 * status code and without providing any user facing details (because this route
 * is not serving the client e.g. when we're calling Mirror through the
 * Publisher)
 */
export class ReploRawHTTPError extends ReploError {
  statusCode: number;
  errorType: string;
  data?: object;

  constructor(config: {
    statusCode: number;
    message: string;
    errorType: string;
    data?: object;
  }) {
    super({
      message: config.message,
      additionalData: { statusCode: config.statusCode, data: config.data },
    });
    this.statusCode = config.statusCode;
    this.errorType = config.errorType;
    this.data = config.data;
  }
}

/**
 * Error which is automatically mapped to a status code response wherever it's
 * thrown. Errors which are thrown from many different places where we want to
 * always respond in the same way should inherit this, e.g. an error thrown for
 * any Shopify call when the user has uninstalled the app.
 *
 * Inheriting from this should be uncommon, for most errors we want to inherit
 * from ReploError and catch/map them to ReploHTTPErrors in our routes. For the
 * uncommon cases where we want to globally map errors this reduces boilerplate.
 *
 * Globally mapped errors are never reported to Sentry or logged.
 */
export class Uncommon_GloballyMappedError extends ReploError {
  userFacingDetails: ErrorUserFacingDetails;
  statusCode: number;

  constructor(
    config: ReploErrorConfig & {
      statusCode: number;
      userFacingDetails: ErrorUserFacingDetails;
    },
  ) {
    super(config);
    this.statusCode = config.statusCode;
    this.userFacingDetails = config.userFacingDetails;
  }
}

/**
 * The `userFacingDetails.key` publisher puts on its 404 for a missing project.
 * It lives here rather than next to the error class because a caller across a
 * service boundary (airlock) matches on it to tell "this project is gone" apart
 * from any other 404 that URL could produce.
 */
export const PROJECT_NOT_FOUND_ERROR_KEY = "errors.project.notFound";

export class ReploValidationError extends ReploError {
  constructor(message: string) {
    super({ message: `[replo-validation-error]: ${message}` });
  }
}

export class UnexpectedMutationError extends ReploError {
  constructor() {
    super({ message: "Unexpected mutation in GraphQL query" });
  }
}

export class UnexpectedRawGraphqlClientError extends ReploError {
  originalError: unknown;
  errors: unknown[];
  data: unknown;

  constructor(opts: {
    originalError: unknown;
    errors: unknown[];
    data: unknown;
  }) {
    const errorMessages = opts.errors
      .map((graphqlError) => {
        if (
          graphqlError &&
          typeof graphqlError === "object" &&
          "message" in graphqlError &&
          typeof graphqlError.message === "string"
        ) {
          return graphqlError.message;
        }
        return JSON.stringify(graphqlError);
      })
      .join("; ");

    super({
      message: errorMessages
        ? `GraphQL error: ${errorMessages}`
        : "Unexpected error from raw GraphQL client",
      cause: opts.originalError,
      additionalData: {
        graphqlErrors: opts.errors,
        graphqlData: opts.data,
      },
    });
    this.originalError = opts.originalError;
    this.errors = opts.errors;
    this.data = opts.data;
  }
}

export const DEFAULT_ERROR_MESSAGE_CONFIG = {
  header: "Unknown Error",
  message: "An unknown error has occurred",
  isFullPage: false,
};

export type ErrorDetails = {
  header: string;
  subheader?: string;
  message?: string | string[] | React.ReactNode;
  messageDetail?: string;
  isRecoverable?: boolean;
  isFullPage?: boolean;
  usage?: {
    current: number;
    maximum: number;
  };
  callToAction?:
    | { type: "installApp" }
    | { type: "callback"; name: string; onClick(): void }
    | { type: "link"; name: string; to: string; target?: string }
    | { type: "closeModal"; name: string }
    | {
        type: "navigate";
        location:
          | { type: "integrationHub" }
          | { type: "home" }
          | {
              type: "workspace";
              workspaceId: string;
              workspaceName: string;
            };
        // Add more navigation locations as needed
      }
    | {
        type: "custom";
        callToActionType: ErrorUserFacingDetailsWithMessage["callToAction"];
      };
};

/**
 * Map of error keys to custom user-facing details. Should ONLY be used
 * in cases where we want to override the default behavior of ErrorUserFacingDetails
 * from the backend.
 *
 * TODO (Noah, 2024-01-15): Clean up these custom configs, all of them
 * are only used by Django (and 'base' can be moved directly into the reducer function)
 */
export const CUSTOM_ERROR_KEY_TO_PROPS = {
  "shopifyArticle.failedToPublish": () => ({
    header: "Shopify was unable to save this Article",
    message: "Check that the Article does not contain invalid liquid code.",
  }),
  "shopifySection.failedToPublish": () => ({
    header: "Shopify was unable to save this Section",
    message: "Check that the Section does not contain invalid liquid code.",
  }),
  "page.failedToPublish": () => ({
    header: "Shopify was unable to save this Page",
    message: "Check that the Page does not contain invalid liquid code.",
  }),
  "shopifyProductTemplate.failedToPublish": () => ({
    header: "Shopify was unable to save this Product Template",
    message: "Check that the Page does not contain invalid liquid code.",
  }),
  "element.failedToPublish": () => ({
    header: "Failed to Publish",
    message: "Something went wrong publishing. Please try again",
  }),
  "element.failedToUpdate": () => ({
    header: "Failed to Autosave",
    message: "Your changes may not have been saved",
  }),
  "store.billingPlan.alreadyExist": () => ({
    header: "Failed to change Plan",
    message: "Same tier subscription already exists for this store",
  }),
  "error.assets.unsupportedFileType": ({ serverMessage }) => ({
    header: "Unsupported file type",
    message:
      serverMessage ?? "Please upload a file with a supported file type.",
  }),
  "error.assets.fileTooLarge": () => ({
    header: "File Too Large",
    message:
      "Theme asset upload too large. Uploading to Files may accommodate larger assets.",
  }),
  "error.files.fileTooLarge": () => ({
    header: "File Too Large",
    message:
      "File uploads must be smaller than 20MB and 20 Megapixels for images, and 1GB for videos. Please upload a smaller file or reach out to support@replo.app for help.",
  }),
  base: () => {
    return DEFAULT_ERROR_MESSAGE_CONFIG;
  },
} satisfies Record<
  string,
  (params: {
    type: string;
    serverMessage?: string;
    endpoint: string | null;
  }) => ErrorDetails | null
>;

export function extractErrorId(error: unknown) {
  if (error instanceof ReploError) {
    return error.id;
  }
  if (error && typeof error === "object") {
    // Legacy cast - may or may not be necessary
    // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
    return (error as any)["reploErrorId"] as string | undefined;
  }
  return undefined;
}

export function addErrorIdIfNeeded(error: unknown) {
  if (error && typeof error === "object" && !extractErrorId(error)) {
    // Legacy cast - may or may not be necessary
    // oxlint-disable-next-line replo/no-as-cast, typescript/no-explicit-any -- legacy any/cast, may or may not be needed
    (error as any)["reploErrorId"] = randomUUID();
  }
}

export const errorUserFacingDetailsMessageSchema = z
  .object({
    type: z.enum([
      "toast",
      "fullPageModal",
      "shopifyStorePassword",
      "billing.addPublishedElements",
      "billing.addShopifyThemeProject",
      "billing.addReploSite",
      // Agent Billing ONLY
      "agentBilling.shopifyThemeBuilder.publishedElements.exceeded",
      "agentBilling.shopifyThemeBuilder.projects.exceeded",
      "agentBilling.sites.exceeded",
      "agentBilling.credits.exceeded",
      "agentBilling.upgradeRequired",
    ]),
    message: z.string(),
    isRecoverable: z.boolean().optional(),
    // NOTE (Matt 2024-03-22): We support detail being an array of strings
    // in FullPageModal only.
    detail: z.array(z.string()).or(z.string()).optional(),
    key: z.string(),
    // Structured data for API callers next to the human message, e.g. the
    // current draft version on a project-context 409 or the offending paths on
    // a 422. The worker error handler serializes userFacingDetails as-is, so
    // this is the one place such data can travel without a second envelope.
    details: z.record(z.string(), z.unknown()).optional(),
    usage: z
      .object({
        current: z.number(),
        maximum: z.number(),
      })
      .optional(),
    plan: z.string().optional(),
    callToAction: z
      .discriminatedUnion("type", [
        z.object({ type: z.literal("reload") }),
        z.object({ type: z.literal("installApp") }),
        z.object({ type: z.literal("goToHomepage") }),
        z.object({ type: z.literal("navigateToProjectEditor") }),
        z.object({ type: z.literal("closeModal") }),
        z.object({
          type: z.literal("navigateToLink"),
          link: z.discriminatedUnion("type", [
            z.object({ type: z.literal("errors.wrongJsonExtension") }),
            z.object({ type: z.literal("errors.chunkingError") }),
            z.object({ type: z.literal("errors.indexBackupError") }),
            z.object({
              type: z.literal("errors.shopifyIntegrationAlreadyAssigned"),
            }),
            z.object({
              type: z.literal("errors.shopifyIntegrationMissingAccessScopes"),
              url: z.string(),
            }),
            z.object({
              type: z.literal("customLink"),
              url: z.string(),
            }),
            z.object({
              type: z.literal(
                "errors.projectConnectedToBillingIntegrationForShopifyWorkspace",
              ),
            }),
            z.object({ type: z.literal("errors.themeAssetSizeLimitExceeded") }),
            z.object({ type: z.literal("errors.themeTotalSizeLimitExceeded") }),
            z.object({
              type: z.literal("errors.pageMetafieldDefinitionsChanged"),
            }),
            z.object({ type: z.literal("settings.billing") }),
          ]),
        }),
      ])
      .optional(),
  })
  .describe("ErrorUserFacingDetailsWithMessage");

/**
 * Schema for user-facing details which the frontend uses to show toasts, modals, etc
 * when an error is thrown from the backend
 */
export const errorUserFacingDetailsSchema = z
  .discriminatedUnion("type", [
    /**
     * Normal errors, show error UI depending on the type
     */
    errorUserFacingDetailsMessageSchema,
    /**
     * Errors which should be ignored by the frontend. This is uncommon, usually
     * you want to show an error in the UI, and if you don't want to then you catch
     * the error in your route.
     */
    z.object({
      type: z.literal("uncommon_none"),
      key: z.string(),
      usage: z
        .object({
          current: z.number(),
          maximum: z.number(),
        })
        .optional(),
      message: z.string().optional(),
      isRecoverable: z.boolean().optional(),
      detail: z.array(z.string()).or(z.string()).optional(),
    }),
  ])
  .describe("ErrorUserFacingDetails");

export const extractErrorTrackingEventDetails = (
  error: unknown,
  transaction: string | undefined,
  tags: Record<string, string> = {},
) => {
  if (error instanceof z.ZodError) {
    const errorId = extractErrorId(error);
    /**
     * NOTE: Ben 2023-11-01 Since most ZodErrors originate from the
     * pre-request logic inside the fastify plugin, Sentry tries to group them
     * together. We want to split them out based on route in the UI though, so
     * we extend the "fingerprint" with the "transaction", which is just the
     * method + path. See
     * https://docs.sentry.io/platforms/node/usage/sdk-fingerprinting/ for
     * more info.
     */
    return {
      fingerprint: ["{{ default }}", transaction ?? ""],
      extra: {
        zodIssues: error.issues,
      },
      tags: {
        ...tags,
        ...(errorId ? { errorId } : {}),
      },
    };
  } else if (error && error instanceof ReploError) {
    const fingerprint = ["{{ default }}"];
    if (error.config.errorReportingDetails?.includeTransactionInFingerprint) {
      fingerprint.push(transaction ?? "");
    }
    if (error.config.errorReportingDetails?.customFingerprint) {
      fingerprint.push(...error.config.errorReportingDetails.customFingerprint);
    }
    const additionalDataStringValues = mapValues(
      error.config.additionalData ?? {},
      (value) => {
        if (typeof value === "string") {
          if (value.length > 1000) {
            return `${value.slice(0, 1000)}...[truncated]`;
          }
          return value;
        }
        return JSON.stringify(value);
      },
    );
    return {
      fingerprint,
      extra: error.config.additionalData,
      tags: { errorId: error.id, ...additionalDataStringValues, ...tags },
    };
  }
  if (error && error instanceof Error && hasOwnProperty(error, "cause")) {
    const errorId = extractErrorId(error);
    return {
      extra: { cause: error.cause },
      tags: { ...tags, ...(errorId ? { errorId } : {}) },
    };
  }
  if (error && typeof error === "object") {
    const errorId = extractErrorId(error);
    return {
      tags: { ...tags, ...(errorId ? { errorId } : {}) },
    };
  }
  return null;
};

export const shouldReportErrorToSentry = (error: Error) => {
  if (
    // Note (Noah, 2024-01-21): Because Got doesn't allow throwing specific types
    // of errors in the beforeError hook, we have to set the cause and map that
    // instead, in order to implement globally mapped errors
    error.cause instanceof ReploHTTPError ||
    error.cause instanceof ReploParseableHTTPError ||
    error.cause instanceof Uncommon_GloballyMappedError ||
    error.cause instanceof ReploValidationError ||
    error.cause instanceof ReploAuthenticationError
  ) {
    return false;
  }
  if (
    error instanceof ReploHTTPError ||
    error instanceof ReploParseableHTTPError ||
    error instanceof Uncommon_GloballyMappedError ||
    error instanceof ReploValidationError ||
    error instanceof ReploAuthenticationError
  ) {
    return false;
  }
  return true;
};

// NOTE (Max, 2026-04-21): The agents framework only sends the error message
// string over the WebSocket — all other fields (class name, additionalData,
// cause) are lost. We wrap our rpc.call with a try/catch that throws this
// error, so that the global MutationCache.onError handler can detect these, and
// show clean toasts instead of dumping raw stack traces via handleNonTRPCClientError.
export class CloudflareAgentCoordinatorRpcError extends ReploError {
  constructor(error: unknown) {
    super({
      message: getErrorMessage(error),
      cause: error,
    });
  }
}

/**
 * Failures on the shared iframe RPC channel (miniapp ↔ parent, canopy ↔ WB).
 * Live next to other domain errors so callers get ReploError reporting without
 * a `replo-utils → schemas` workspace cycle (schemas already depends on
 * replo-utils; the channel implementation lives in `schemas/iframeRpcChannel`).
 */
export abstract class IframeRpcChannelError extends ReploError {}

export class IframeRpcChannelTimeoutError extends IframeRpcChannelError {
  constructor({ method, timeoutMs }: { method: string; timeoutMs: number }) {
    super({
      message: `RPC method '${method}' timed out after ${timeoutMs}ms`,
      additionalData: { method, timeoutMs },
    });
  }
}

export class IframeRpcChannelTargetUnavailableError extends IframeRpcChannelError {
  constructor({
    callType,
    method,
  }: {
    callType: "notify" | "request" | "response";
    method?: string;
  }) {
    super({
      message: method
        ? `RPC target window was unavailable for '${method}'`
        : "RPC target window was unavailable for a response",
      additionalData: { callType, method },
    });
  }
}

export class IframeRpcChannelRemoteError extends IframeRpcChannelError {
  constructor({ method, message }: { method: string; message: string }) {
    super({
      message: `RPC method '${method}' failed: ${message}`,
      additionalData: { method },
    });
  }
}

export class IframeRpcChannelUnsubscribedError extends IframeRpcChannelError {
  constructor({ method }: { method: string }) {
    super({
      message: `RPC was unsubscribed while '${method}' was pending`,
      additionalData: { method },
    });
  }
}

export class IframeRpcChannelUnknownMethodError extends IframeRpcChannelError {
  constructor({ method }: { method: string }) {
    super({
      message: `Unknown RPC method '${method}'`,
      additionalData: { method },
    });
  }
}

export class IframeRpcChannelConfigurationError extends IframeRpcChannelError {
  constructor({ message }: { message: string }) {
    super({ message });
  }
}

export class IframeRpcChannelInvalidInputError extends IframeRpcChannelError {
  constructor({ method, cause }: { method: string; cause: z.ZodError }) {
    super({
      message: `Invalid RPC input for '${method}': ${cause.message}`,
      cause,
      additionalData: { method },
    });
  }
}
